/* @jsxImportSource react */
'use client';

import {
  type ComponentPropsWithoutRef,
  createContext,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { ComponentRenderFn, DialogCloseState, HTMLProps } from '@base-ui/react';
import { AlertDialog as AlertDialogBase } from '@base-ui/react/alert-dialog';
import { Dialog as DialogBase } from '@base-ui/react/dialog';
import { IconX } from '@tabler/icons-react';

import { cn } from '../../utils/cn';
import { type PortalContainer, usePortalContainer } from '../../utils/portal-provider';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';
import { Box, Row } from '../box/box';
import { Button } from '../button/button';

/** Dialog size variant definitions mapping sizes to their minimum widths. */
export const ATOMS_DIALOG_VARIANTS = {
  size: {
    base: {
      classes: 'sm:min-w-96',
      description: 'Default dialog width',
    },
    sm: {
      classes: 'sm:min-w-72',
      description: 'Small dialog for simple confirmations',
    },
    lg: {
      classes: 'sm:min-w-[32rem]',
      description: 'Large dialog for complex content',
    },
    xl: {
      classes: 'sm:min-w-[48rem]',
      description: 'Extra large dialog for detailed views',
    },
  },
  role: {
    dialog: {
      classes: '',
      description: 'Standard dialog for general-purpose modals',
    },
    alertdialog: {
      classes: '',
      description: 'Alert dialog for confirmation flows requiring explicit user acknowledgment',
    },
  },
} as const;

export const ATOMS_DIALOG_DEFAULT_VARIANTS = {
  size: 'base',
  role: 'dialog',
} as const;

// Desktop (sm:) values — width is the size's min-width; the title is
// text-header-lg (45px / 500) at every size (text-subheader-lg 32px on
// mobile); padding is p-12 (48px) on desktop, p-8 (32px) on mobile.
export const ATOMS_DIALOG_STYLING = {
  dimensions: {
    sm: {
      width: 288,
      titleSize: 45,
      descSize: 16,
      padding: 48,
      gap: 24,
      buttonSize: 'sm',
    },
    base: {
      width: 384,
      titleSize: 45,
      descSize: 16,
      padding: 48,
      gap: 24,
      buttonSize: 'base',
    },
    lg: {
      width: 512,
      titleSize: 45,
      descSize: 16,
      padding: 48,
      gap: 24,
      buttonSize: 'base',
    },
    xl: {
      width: 768,
      titleSize: 45,
      descSize: 16,
      padding: 48,
      gap: 24,
      buttonSize: 'base',
    },
  },
  baseTokens: {
    background: 'color-surface',
    text: 'text-color-surface',
    borderRadius: 32,
    // Mirrors the `shadow-lg` utility on the panel.
    shadow: 'shadow-lg',
  },
  backdrop: {
    background: 'color-od-bg-scrim',
    opacity: 0.72,
  },
  header: {
    title: { fontWeight: 500, color: 'text-color-surface' },
    closeIcon: { name: 'tabler-x', size: 24, color: 'text-color-surface' },
  },
  description: {
    fontWeight: 400,
    color: 'text-color-surface',
  },
  buttons: {
    primary: { background: 'color-primary', text: 'white' },
    secondary: { ring: 'color-border', text: 'text-color-surface' },
  },
} as const;

// Derived types from ATOMS_DIALOG_VARIANTS
export type AtomsDialogSize = keyof typeof ATOMS_DIALOG_VARIANTS.size;
export type AtomsDialogRole = keyof typeof ATOMS_DIALOG_VARIANTS.role;

export interface AtomsDialogVariantsProps {
  /**
   * Dialog width.
   * - `"sm"` — Small (min 288px) for simple confirmations
   * - `"base"` — Default (min 384px)
   * - `"lg"` — Large (min 512px) for complex content
   * - `"xl"` — Extra large (min 768px) for detailed views
   * @default "base"
   */
  size?: AtomsDialogSize;
}

// ============================================================================
// Dialog Role Context
// ============================================================================

const DialogRoleContext = createContext<AtomsDialogRole>('dialog');

function useDialogRole() {
  return useContext(DialogRoleContext);
}

export function dialogVariants({
  size = ATOMS_DIALOG_DEFAULT_VARIANTS.size,
}: AtomsDialogVariantsProps = {}) {
  return cn(
    // Shared surface styles (match Bricks Next: no border, surface background, soft shadow)
    'fixed bg-od-bg-primary text-od-content-primary shadow-lg duration-150 transition-[translate,scale,opacity]',
    // Mobile (Bricks Next parity): full-width sheet anchored to the bottom edge with
    // rounded top corners that slides up from the bottom on open and back down on close.
    // Overflow stays visible so the drag handle can float just above the sheet.
    'inset-x-0 bottom-0 w-full max-w-none translate-y-0 rounded-t-[2rem] max-sm:data-starting-style:translate-y-full max-sm:data-ending-style:translate-y-full',
    // Desktop (sm+): centered modal with a scale + fade transition; clipped to its corners.
    'sm:inset-x-auto sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:w-auto sm:max-w-[calc(100vw-3rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:overflow-hidden sm:rounded-[2rem] sm:data-starting-style:scale-90 sm:data-starting-style:opacity-0 sm:data-ending-style:scale-90 sm:data-ending-style:opacity-0',
    // Per-size min-width (desktop only — the mobile sheet is always full-width)
    ATOMS_DIALOG_VARIANTS.size[size].classes,
  );
}

/**
 * Dialog component props — the modal content panel.
 *
 * @example Built-in title and close button (the close button shows by default; pass `hideCloseButton` to remove it)
 * ```tsx
 * <Dialog.Root>
 *   <Dialog.Trigger render={(p) => <Button aria-label="Open" analyticsName="app-open-dialog" {...p}>Open</Button>} />
 *   <Dialog title="Confirm Action">
 *     <Dialog.Description>Are you sure?</Dialog.Description>
 *     <Dialog.Close render={(p) => <Button aria-label="Cancel" analyticsName="app-cancel" {...p}>Cancel</Button>} />
 *   </Dialog>
 * </Dialog.Root>
 * ```
 */
export type DialogProps = AtomsDialogVariantsProps & {
  /** Additional CSS classes merged via `cn()`. */
  className?: string;
  /** Dialog content (typically Description, Close, and action buttons). */
  children: ReactNode;
  /**
   * Optional heading rendered at the top of the dialog with the standard title
   * typography. Equivalent to composing a `Dialog.Title` yourself; provide this
   * for the common case. Omit it to lay out your own header.
   */
  title?: ReactNode;
  /**
   * Hide the built-in close (X) button. A close button is rendered in the
   * top-right corner by default (a `Button` wired to `Dialog.Close`); set this
   * to `true` to remove it — e.g. for alert dialogs that force an explicit choice.
   * @default false
   */
  hideCloseButton?: boolean;
  /**
   * Container element for the portal. Use this to render the dialog inside
   * a Shadow DOM or custom container. Overrides `AtomsPortalProvider` context.
   * @default document.body (or AtomsPortalProvider container if set)
   */
  container?: PortalContainer;
};

/** Distance in px the sheet must be dragged down before it dismisses on release. */
const SHEET_DRAG_DISMISS_THRESHOLD = 96;

/**
 * Drag-to-dismiss for the mobile bottom sheet. Tracks a downward drag on the
 * sheet handle, following the finger while dragging, and clicks the hidden
 * `Dialog.Close` once the drag passes the threshold; otherwise it snaps back.
 */
function useSheetDragDismiss() {
  const closeRef = useRef<HTMLButtonElement>(null);
  const startYRef = useRef(0);
  const cleanupRef = useRef<() => void>(() => {});
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Detach any window listeners if the dialog unmounts mid-drag.
  useEffect(() => () => cleanupRef.current(), []);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    startYRef.current = event.clientY;
    setIsDragging(true);
    setDragOffset(0);

    // Track the drag on the window so it keeps following the pointer even after it
    // leaves the small handle hit area (more robust than setPointerCapture).
    const handleMove = (moveEvent: PointerEvent) => {
      setDragOffset(Math.max(0, moveEvent.clientY - startYRef.current));
    };
    const handleUp = (upEvent: PointerEvent) => {
      cleanupRef.current();
      setIsDragging(false);
      setDragOffset(0);
      if (upEvent.clientY - startYRef.current > SHEET_DRAG_DISMISS_THRESHOLD) {
        closeRef.current?.click();
      }
    };
    cleanupRef.current = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
      cleanupRef.current = () => {};
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
  };

  return {
    closeRef,
    // Follow the finger 1:1 with no transition while dragging; once released the inline
    // style is dropped so the class transition animates the snap-back or the dismissal.
    sheetStyle: isDragging
      ? ({ translate: `0 ${dragOffset}px`, transition: 'none' } as const)
      : undefined,
    dragHandleProps: { onPointerDown },
  };
}

const renderCloseButton: ComponentRenderFn<HTMLProps, DialogCloseState> = (props) => (
  <Button
    {...props}
    variant="secondary"
    size="sm"
    aria-label="Close"
    analyticsName="atoms-dialog-close"
    className="absolute top-6 right-6 h-11 w-11 rounded-full border-0 p-0 max-sm:hidden"
  >
    <IconX size={24} />
  </Button>
);

/**
 * Modal dialog overlay with backdrop. Compound component with `Dialog.Root`,
 * `Dialog.Trigger`, `Dialog.Title`, `Dialog.Description`, and `Dialog.Close`.
 *
 * @example
 * ```tsx
 * <Dialog.Root>
 *   <Dialog.Trigger render={(p) => <Button aria-label="Delete" analyticsName="app-delete" {...p}>Delete</Button>} />
 *   <Dialog className="p-8">
 *     <Dialog.Title>Delete Item</Dialog.Title>
 *     <Dialog.Description>This action cannot be undone.</Dialog.Description>
 *     <Dialog.Close render={(p) => <Button aria-label="Delete" analyticsName="app-confirm-delete" {...p}>Delete</Button>} />
 *   </Dialog>
 * </Dialog.Root>
 * ```
 *
 * @example Alert Dialog for destructive actions
 * ```tsx
 * <Dialog.Root role="alertdialog">
 *   <Dialog.Trigger render={(p) => <Button aria-label="Delete Project" analyticsName="app-delete-project" {...p}>Delete Project</Button>} />
 *   <Dialog className="p-8">
 *     <Dialog.Title>Delete Project?</Dialog.Title>
 *     <Dialog.Description>This action cannot be undone.</Dialog.Description>
 *     <Dialog.Close render={(p) => <Button aria-label="Cancel" analyticsName="app-cancel" variant="secondary" {...p}>Cancel</Button>} />
 *     <Dialog.Close render={(p) => <Button aria-label="Delete" analyticsName="app-confirm-delete" {...p}>Delete</Button>} />
 *   </Dialog>
 * </Dialog.Root>
 * ```
 */
function DialogContent({
  className,
  children,
  size = ATOMS_DIALOG_DEFAULT_VARIANTS.size,
  title,
  hideCloseButton = false,
  container: containerProp,
}: DialogProps) {
  const role = useDialogRole();
  const contextContainer = usePortalContainer();
  const container = containerProp ?? contextContainer ?? undefined;

  const BasePortal = role === 'alertdialog' ? AlertDialogBase.Portal : DialogBase.Portal;
  const BaseBackdrop = role === 'alertdialog' ? AlertDialogBase.Backdrop : DialogBase.Backdrop;
  const BasePopup = role === 'alertdialog' ? AlertDialogBase.Popup : DialogBase.Popup;

  const { closeRef, sheetStyle, dragHandleProps } = useSheetDragDismiss();

  const renderCloseTarget = useCallback<ComponentRenderFn<HTMLProps, DialogCloseState>>(
    (props) => (
      <Button
        {...props}
        ref={closeRef}
        tabIndex={-1}
        aria-hidden="true"
        aria-label="Close"
        analyticsName="atoms-dialog-drag-close"
        className="hidden"
      />
    ),
    [closeRef],
  );

  return (
    <BasePortal container={container}>
      <BaseBackdrop className="fixed inset-0 bg-od-bg-scrim/[0.72] transition-all duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0" />
      <BasePopup
        className={cn(dialogVariants({ size }), 'p-8 sm:p-12', className)}
        style={sheetStyle}
      >
        {!hideCloseButton && (
          <>
            {/* Mobile: a Bricks Next-style drag handle floating just above the sheet.
                Drag it down past the threshold to dismiss; release short to snap back. */}
            <Row
              {...dragHandleProps}
              className="absolute -top-7 left-1/2 z-10 h-8 w-24 -translate-x-1/2 cursor-grab touch-none items-center justify-center active:cursor-grabbing sm:hidden"
            >
              {/* Matches the Bricks Next sheet handle: 88×8, fully rounded, white @ 0.72. */}
              <Box aria-hidden="true" className="h-2 w-22 rounded-full bg-od-bg-primary/72" />
            </Row>
            {/* Hidden close target clicked when the sheet is dragged past the threshold. */}
            <DialogClose render={renderCloseTarget} />
            {/* Desktop: a close (X) button in the top-right corner. */}
            <DialogClose aria-label="Close" render={renderCloseButton} />
          </>
        )}
        {title != null && (
          <DialogTitle className={cn('pb-4 sm:pb-6', !hideCloseButton && 'sm:pr-8')}>
            {title}
          </DialogTitle>
        )}
        {children}
      </BasePopup>
    </BasePortal>
  );
}

// ============================================================================
// Dialog Root
// ============================================================================

type StandardDialogRole = {
  /**
   * The ARIA role for the dialog.
   * - `"dialog"` — Standard dialog for general-purpose modals. Dismissible via outside click by default.
   * - `"alertdialog"` — Alert dialog for destructive or confirmation flows. Not dismissible via outside click.
   *
   * Use `role="alertdialog"` for:
   * - Destructive actions (delete, discard, remove)
   * - Confirmation dialogs requiring explicit user acknowledgment
   * - Actions that cannot be undone
   *
   * @default "dialog"
   */
  role: 'dialog';
};

type AlertDialogRole = {
  /**
   * The ARIA role for the dialog.
   * - `"dialog"` — Standard dialog for general-purpose modals. Dismissible via outside click by default.
   * - `"alertdialog"` — Alert dialog for destructive or confirmation flows. Not dismissible via outside click.
   *
   * Use `role="alertdialog"` for:
   * - Destructive actions (delete, discard, remove)
   * - Confirmation dialogs requiring explicit user acknowledgment
   * - Actions that cannot be undone
   *
   * @default "dialog"
   */
  role: 'alertdialog';
};

type StandardDialogRootProps = Omit<
  ComponentPropsWithoutRef<typeof DialogBase.Root>,
  'role' | 'style'
> &
  StandardDialogRole;

type AlertDialogRootProps = Omit<
  ComponentPropsWithoutRef<typeof AlertDialogBase.Root>,
  'role' | 'style'
> &
  AlertDialogRole;

export type DialogRootProps = StandardDialogRootProps | AlertDialogRootProps;

function DialogRoot(props: DialogRootProps) {
  if (props.role === 'alertdialog') {
    const { role, children, ...rest } = props;
    return (
      <DialogRoleContext.Provider value={role}>
        <AlertDialogBase.Root {...rest}>{children}</AlertDialogBase.Root>
      </DialogRoleContext.Provider>
    );
  }
  const { role, children, ...rest } = props;
  return (
    <DialogRoleContext.Provider value={role}>
      <DialogBase.Root {...rest}>{children}</DialogBase.Root>
    </DialogRoleContext.Provider>
  );
}

DialogRoot.displayName = 'Dialog.Root';

// ============================================================================
// Dialog Trigger
// ============================================================================

export type DialogTriggerProps = Omit<
  ComponentPropsWithoutRef<typeof DialogBase.Trigger>,
  'handle' | 'style'
>;

function DialogTrigger({ children, ...props }: DialogTriggerProps) {
  const role = useDialogRole();
  const BaseTrigger = role === 'alertdialog' ? AlertDialogBase.Trigger : DialogBase.Trigger;
  return <BaseTrigger {...props}>{children}</BaseTrigger>;
}

DialogTrigger.displayName = 'Dialog.Trigger';

// ============================================================================
// Dialog Title
// ============================================================================

type BaseDialogTitleProps = StripUndesiredProps<ComponentPropsWithoutRef<typeof DialogBase.Title>>;

export type DialogTitleProps = BaseDialogTitleProps;

function DialogTitle({ className, ...props }: DialogTitleProps) {
  const role = useDialogRole();
  const BaseTitle = role === 'alertdialog' ? AlertDialogBase.Title : DialogBase.Title;
  // Match Bricks Next: subheaderLarge on small screens, headerLarge from sm up.
  return (
    <BaseTitle
      className={cn('text-subheader-lg text-od-content-primary sm:text-header-lg', className)}
      {...props}
    />
  );
}

DialogTitle.displayName = 'Dialog.Title';

// ============================================================================
// Dialog Description
// ============================================================================

type BaseDialogDescriptionProps = StripUndesiredProps<
  ComponentPropsWithoutRef<typeof DialogBase.Description>
>;

export type DialogDescriptionProps = BaseDialogDescriptionProps;

function DialogDescription({ className, ...props }: DialogDescriptionProps) {
  const role = useDialogRole();
  const BaseDescription =
    role === 'alertdialog' ? AlertDialogBase.Description : DialogBase.Description;
  return <BaseDescription className={className} {...props} />;
}

DialogDescription.displayName = 'Dialog.Description';

// ============================================================================
// Dialog Close
// ============================================================================

type BaseDialogCloseProps = StripUndesiredProps<ComponentPropsWithoutRef<typeof DialogBase.Close>>;

export type DialogCloseProps = BaseDialogCloseProps;

function DialogClose({ children, ...props }: DialogCloseProps) {
  const role = useDialogRole();
  const BaseClose = role === 'alertdialog' ? AlertDialogBase.Close : DialogBase.Close;
  return <BaseClose {...props}>{children}</BaseClose>;
}

DialogClose.displayName = 'Dialog.Close';

// ============================================================================
// Compound Component Export
// ============================================================================

const Dialog = Object.assign(DialogContent, {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Title: DialogTitle,
  Description: DialogDescription,
  Close: DialogClose,
});

export { Dialog, DialogRoot, DialogTrigger, DialogTitle, DialogDescription, DialogClose };
